import {
    TASK_GET_ALL,
    TASK_SET_FILTERS,
    TASK_CLEAR_FILTERS,
    TASK_SET_LOADING,
    TASK_SET_ERROR,
    TASK_SET_EMPLOYEES,
    TASK_CREATE_SUCCESS,
    TASK_CREATE_FAILURE,
    CREATE_WORK_LOG_FAILURE,
    CREATE_WORK_LOG_SUCCESS,
    CREATE_WORK_LOG_REQUEST
} from "./TaskActionType";

const apiConfig = window as any;

/* ------------------- BASE API URL ------------------- */

const BASE_API_URL =
    apiConfig.BASE_API_URL?.charAt(apiConfig.BASE_API_URL.length - 1) === "/"
        ? apiConfig.BASE_API_URL.substring(0, apiConfig.BASE_API_URL.length - 1)
        : apiConfig.BASE_API_URL;

console.log("Task API URL:", BASE_API_URL);

const BasicAuthentication = apiConfig.BasicAuthentication;

/* ------------------- HEADERS ------------------- */

const postAuthorizedHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(BasicAuthentication
        ? { Authorization: "Basic " + btoa(BasicAuthentication) }
        : {})
};

/* ============================================================
   INTERFACES – Matching your actual backend response
============================================================ */

export interface TaskFilterRequest {
    EmployeeCode?: string;
}

// Updated interface for the API response format
export interface TaskDetailsResponse {
    TaskID: string;
    TaskName: string;
    TaskDescription?: string;
    TaskTaken: string;
    TaskGivenByName: string;
    Status: string;
    BlockerStatus: string;
    WorklogDisplay: string;
    StartDateDisplay: string;
    EndDateDisplay: string;
    DeliveryUnit: string;
    PriorityDisplay: string;
    Actions: string;
    TotalWorklogHours: number;
    WorklogDetails: any;
    CommentDetails: any;
    TotalCall: number;
}

export interface Employee {
    ID: number;
    EmployeeID: number;
    EmployeeCode: string;
    EmployeeName: string;
    Email: string;
    MobileNumber: string;
    Department: string;
    Designation: string;
    EmploymentStatus: string;
    IsActive: boolean;
}

export interface TaskApiResponse {
    isSuccess: boolean;
    message: string;
    accessedOn: string | null;
    records: TaskDetailsResponse[];
    emplopyelist?: Employee[];
}

export interface CreateTaskResponse {
    success: boolean;
    message: string;
    taskID: string;
}

/* ============================================================
   Helper: Convert API response to frontend Task object
============================================================ */

function getMainTaskId(taskId: string | undefined): string {
    if (!taskId || typeof taskId !== 'string') {
        return 'MT-000';
    }
    const match = taskId.match(/\d+/);
    if (match) {
        const num = parseInt(match[0]);
        const mainNum = Math.floor(num / 1000) * 1000;
        return `MT-${String(mainNum).padStart(3, '0')}`;
    }
    return 'MT-000';
}

// Helper function to convert DD/MM/YYYY to YYYY-MM-DD for date input
function convertDateToInputFormat(dateStr: string): string {
    if (!dateStr) return '';
    // If already in YYYY-MM-DD format, return as is
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
    // Convert DD/MM/YYYY to YYYY-MM-DD
    const parts = dateStr.split('/');
    if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateStr;
}

// Helper function to extract employee code from formatted string like "Name (EMP001)"
function extractEmployeeCode(formattedString: string): string {
    if (!formattedString) return '';
    const match = formattedString.match(/\(([^)]+)\)/);
    return match ? match[1] : '';
}

// Helper function to extract employee name from formatted string like "Name (EMP001)"
function extractEmployeeName(formattedString: string): string {
    if (!formattedString) return '';
    const match = formattedString.match(/^([^(]+)/);
    return match ? match[1].trim() : formattedString;
}

function convertApiTask(apiTask: TaskDetailsResponse): any {
    // Extract plain task ID (remove TK- prefix if present)
    let plainTaskId = apiTask.TaskID;
    if (plainTaskId && plainTaskId.startsWith('TK-')) {
        plainTaskId = plainTaskId.replace('TK-', '');
    }
    
    return {
        taskId: plainTaskId,
        mainTaskId: getMainTaskId(apiTask.TaskID),
        taskName: apiTask.TaskName,
        task: apiTask.TaskName,
        taskDescription: apiTask.TaskDescription || '',
        taskTaken: extractEmployeeName(apiTask.TaskTaken) || apiTask.TaskTaken || '',
        taskGivenTo: extractEmployeeCode(apiTask.TaskTaken),  // Extract EMP006 from "D D (EMP006)"
        taskGivenToName: apiTask.TaskTaken || '',
        taskGivenBy: extractEmployeeCode(apiTask.TaskGivenByName),  // Extract EMP005 from "Gaurav Anand (EMP005)"
        taskGivenByName: apiTask.TaskGivenByName || '',
        status: apiTask.Status,
        anyBlocker: apiTask.BlockerStatus === '🔘' ? 'Has blocker' : '',
        worklogHour: parseFloat(apiTask.WorklogDisplay) || 0,
        startDate: convertDateToInputFormat(apiTask.StartDateDisplay || ''),
        endDate: convertDateToInputFormat(apiTask.EndDateDisplay || ''),
        phone: '',
        deliveryUnit: apiTask.DeliveryUnit || '',
        priority: (apiTask.PriorityDisplay?.toLowerCase() as any) || 'medium',
        openWU: 0,
        logNo: '',
        estimatedHours: 0,
        actualHours: 0,
        progressPercentage: 0,
        isOverdue: false,
        daysRemaining: 0,
        totalLoggedHours: apiTask.TotalWorklogHours || 0,
        workLogCount: 0,
        totalCall: apiTask.TotalCall || 0,
        workLogs: [],
        comments: []
    };
}

/* ============================================================
   GET TASK DETAILS
============================================================ */

export const GetTaskDetails =
    (employeeCode?: string) => async (dispatch: any) => {
        dispatch({ type: TASK_SET_LOADING, payload: true });

        try {
            const requestBody: TaskFilterRequest = {};
            if (employeeCode) {
                requestBody.EmployeeCode = employeeCode;
            }

            console.log("Fetching task details:", requestBody);

            const response = await fetch(`${BASE_API_URL}/TaskDetails`, {
                method: "POST",
                headers: postAuthorizedHeaders,
                body: JSON.stringify(requestBody)
            });

            const data: TaskApiResponse = await response.json();
            console.log("Task API response:", data);

            dispatch({ type: TASK_SET_LOADING, payload: false });

            if (data?.emplopyelist) {
                dispatch({
                    type: TASK_SET_EMPLOYEES,
                    payload: data.emplopyelist
                });
            }

            if (data?.isSuccess) {
                const rawTasks = data.records || [];
                const convertedTasks = rawTasks.map(convertApiTask);
                
                console.log("Converted tasks:", convertedTasks);
                
                dispatch({
                    type: TASK_GET_ALL,
                    payload: convertedTasks
                });

                return { tasks: convertedTasks, employees: data.emplopyelist || [], isSuccess: true };
            }

            dispatch({
                type: TASK_GET_ALL,
                payload: []
            });

            return { tasks: [], employees: data?.emplopyelist || [], isSuccess: false };
            
        } catch (err) {
            console.error("GetTaskDetails error:", err);
            dispatch({ type: TASK_SET_LOADING, payload: false });
            dispatch({
                type: TASK_SET_ERROR,
                payload: {
                    success: false,
                    message: "Failed to fetch tasks",
                    error: err
                }
            });
            dispatch({
                type: TASK_GET_ALL,
                payload: []
            });
            return { tasks: [], employees: [], isSuccess: false };
        }
    };

/* ============================================================
   CREATE TASK
============================================================ */
export const CreateTask = (taskData: any) => async (dispatch: any) => {
    dispatch({ type: TASK_SET_LOADING, payload: true });

    try {
        // Prepare work_logs array
        let workLogs: any = [];
        if (taskData.workDate && taskData.description && taskData.worklogHour > 0) {
            workLogs = [{
                work_date: taskData.workDate,
                hours_logged: taskData.worklogHour,
                description: taskData.description,
                employee_code: taskData.taskGivenTo || taskData.changedBy
            }];
        }

        // Prepare comments array
        let comments : any= [];
        if (taskData.description && taskData.changedBy) {
            comments = [{
                comment: taskData.description,
                employee_code: taskData.changedBy
            }];
        }

        // Convert taskId to plain number (remove TK- prefix if present)
        let plainTaskId = taskData.taskId;
        if (plainTaskId && typeof plainTaskId === 'string' && plainTaskId.startsWith('TK-')) {
            plainTaskId = plainTaskId.replace('TK-', '');
        }

        const payload = {
            action: taskData.action || "I",
            taskId: plainTaskId || null,
            taskName: taskData.taskName || "",
            taskDescription: taskData.taskDescription || "",
            taskTaken: taskData.taskTaken || "",
            taskGivenTo: taskData.taskGivenTo || "",
            taskGivenBy: taskData.taskGivenBy || "",
            status: taskData.status || "Open",
            anyBlocker: taskData.anyBlocker || "",
            worklogHour: taskData.worklogHour || 0,
            startDate: taskData.startDate || null,
            endDate: taskData.endDate || null,
            phone: taskData.phone || "",
            deliveryUnit: taskData.deliveryUnit || "",
            priority: taskData.priority || "medium",
            openWu: taskData.openWu || 0,
            logNo: taskData.logNo || "",
            estimatedHours: taskData.estimatedHours || 0,
            actualHours: taskData.actualHours || 0,
            work_logs: workLogs,
            comments: comments,
            changedBy: taskData.changedBy || "",
            totalCall: taskData.totalCall || 0
        };

        console.log("Create Task Payload:", payload);

        const response = await fetch(`${BASE_API_URL}/CreateTask`, {
            method: "POST",
            headers: postAuthorizedHeaders,
            body: JSON.stringify(payload)
        });

        const data = await response.json();
        console.log("Create Task Response:", data);

        dispatch({ type: TASK_SET_LOADING, payload: false });

        if (data.success) {
            dispatch({
                type: TASK_CREATE_SUCCESS,
                payload: data
            });
            dispatch(GetTaskDetails());
            return { success: true, taskId: data.task_id, message: data.message };
        } else {
            dispatch({
                type: TASK_CREATE_FAILURE,
                payload: data.message || data.error
            });
            return { success: false, message: data.message || data.error };
        }
    } catch (error) {
        console.error("CreateTask Error:", error);
        dispatch({ type: TASK_SET_LOADING, payload: false });
        dispatch({
            type: TASK_CREATE_FAILURE,
            payload: "Task creation failed"
        });
        return { success: false, message: "Task creation failed" };
    }
};

/* ============================================================
   FILTER ACTIONS
============================================================ */

export const SetTaskFilters = (filters: TaskFilterRequest) => ({
    type: TASK_SET_FILTERS,
    payload: filters
});

export const ClearTaskFilters = () => ({
    type: TASK_CLEAR_FILTERS
});

/* ============================================================
   CREATE WORK LOG
============================================================ */

export const CreateWorkLog = (workLogData: any) => async (dispatch: any) => {
    dispatch({ type: CREATE_WORK_LOG_REQUEST, payload: true });
    
    try {
        // Convert task_id to plain number (remove TK- prefix if present)
        let plainTaskId = workLogData.task_id;
        if (plainTaskId && typeof plainTaskId === 'string' && plainTaskId.startsWith('TK-')) {
            plainTaskId = plainTaskId.replace('TK-', '');
        }

        const payload = {
            log_id: workLogData.log_id,
            log_no: workLogData.log_no || null,
            task_id: plainTaskId,
            employee_code: workLogData.employee_code || null,
            work_date: workLogData.work_date,
            hours_logged: workLogData.hours_logged,
            description: workLogData.description || '',
            total_call: workLogData.total_call || 0
        };

        console.log("Create Work Log Payload:", payload);
        
        const response = await fetch(`${BASE_API_URL}/CreateWorklog`, {
            method: 'POST',
            headers: postAuthorizedHeaders,
            body: JSON.stringify(payload)
        });
        
        const data = await response.json();
        console.log("Create Work Log Response:", data);
        
        if (data.success) {
            dispatch({ 
                type: CREATE_WORK_LOG_SUCCESS, 
                payload: data 
            });
            dispatch(GetTaskDetails());
            return { success: true, log_id: data.log_id, message: data.message || 'Work log created successfully' };
        } else {
            dispatch({ 
                type: CREATE_WORK_LOG_FAILURE, 
                payload: data.message || 'Failed to create work log' 
            });
            return { success: false, message: data.message || 'Failed to create work log' };
        }
    } catch (error) {
        console.error("CreateWorkLog Error:", error);
        dispatch({ 
            type: CREATE_WORK_LOG_FAILURE, 
            payload: error instanceof Error ? error.message : 'Network error' 
        });
        return { success: false, message: error instanceof Error ? error.message : 'Network error' };
    }
};